import FlowContainer from "@/components/pages/AppPages/Pages/components/FlowContainer";

import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useOrderTracking } from "./OrderTrackingMachine";
import { styled } from "@mui/material";
import { getEntityImage } from "@/utils/StringUtils";
import DotAnimation from "./DotAnimation";
import { mobileHeaderHeight } from "@/components/StoreComponents/StoreSinglePageOutlet";
import { MOBILE_SCREEN_SIZE } from "@/components/AppNavigation/constants";
import { TABLET_SCREEN_SIZE } from "@/components/AppNavigation/constants";
import {
  AnalyticsTrackingEvent,
  getRetailerFromId,
} from "@brandclub/common-ui";
import { SolidButton } from "@/components/StoreComponents/StoreButton";
import {
  UserInteractionRequirement,
  UserInteractionType,
  UserRetailerCheckoutOrder,
  UserRetailerCheckoutOrderStatusV2,
} from "@/components/Checkout/types";
import EditConnectPopup, {
  PopupStatus,
} from "../CheckoutForm/components/retailerConnect/EditConnectPopup";
import { useAppSelector } from "@/redux/hooks";
import { OTPPopup } from "../CheckoutForm/components/retailerConnect/EditConnectPopup/OTP";
import { UserRetailerCheckoutCommunicationResponse } from "@/components/Checkout/CheckoutMachine/types/FastCheckoutMachine";
import { useSiteContext } from "@/components/SiteProvider";
import { useTrackActions } from "@/utils/hooks/useTracking";

interface OrderConfirmationProps {
  orderNumber?: string;
  customerName?: string;
  shippingAddress?: string;
  estimatedDeliveryDate?: Date;
  savedAmount?: number;
  savedAmountReason?: string;
  productImageUrl?: string;
}

const getRetailerIdFromOrderId = (orderId: string) => {
  const retailerId = orderId.split("-")[0];
  return retailerId;
};

const RetailerConnectContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 50,
  marginBottom: "5%",
  ".icons": {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 44,
    img: {
      width: 84,
      height: 84,
    },
    [theme.breakpoints.down("md")]: {
      gap: 20,
      img: {
        width: 57,
        height: 57,
      },
    },
  },
  ".desc": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    maxWidth: 460,
    gap: 10,
    ".title": {
      textAlign: "center",
      fontSize: 24,
      fontWeight: 700,
      color: theme.vars.palette.text.primary,
    },
    ".desc": {
      textAlign: "center",
      fontSize: 16,
      fontWeight: 500,
      color: theme.vars.palette.text.primary,
    },
    ".switch-to-guest": {
      cursor: "pointer",
      textAlign: "center",
      fontSize: 16,
      fontWeight: 500,
      color: theme.vars.palette.text.primary,
      textDecoration: "underline",
    },
  },
}));

const RetailerConnection = ({
  orderData,
  interactionRequirement,
  submitUserInteraction,
  isTimeOut,
  submitUserInteractionAndRetry,
  retryCheckout,
  switchToGuest,
}: {
  isTimeOut: boolean;
  orderData?: UserRetailerCheckoutOrder;
  interactionRequirement?: UserInteractionRequirement;
  submitUserInteraction: (interactionData: {
    interactionType: UserInteractionType;
    interactionData: UserRetailerCheckoutCommunicationResponse;
  }) => void;
  submitUserInteractionAndRetry: (interactionData: {
    interactionType: UserInteractionType;
    interactionData: UserRetailerCheckoutCommunicationResponse;
  }) => void;
  retryCheckout: () => void;
  switchToGuest: () => void;
}) => {
  const [trackAction] = useTrackActions();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");
  const retailerId = getRetailerIdFromOrderId(orderId || "");
  const [otpOpen, setOtpOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [resendCoolDown, setResendCoolDown] = useState(0);

  // const userProfile = useAppSelector((state) => state.userProfile);

  // const { treatments } = useSplitTreatments({
  //   names: ["brandclub_guest_checkout"],
  //   attributes: {
  //     email: userProfile?.email ?? "",
  //   },
  // });

  // const experimentEnabled =
  //   treatments["brandclub_guest_checkout"].treatment === "experiment";

  // const enabledGuestCheckout =
  //   treatments["brandclub_guest_checkout"].treatment === "on" ||
  //   experimentEnabled;
  useEffect(() => {
    if (resendCoolDown > 0) {
      setTimeout(() => setResendCoolDown(resendCoolDown - 1), 1000);
    }
  }, [resendCoolDown]);

  const retailers = useAppSelector(({ retailers }) => retailers);
  const currentRetailer = retailers.find((r) => r.retailerId === +retailerId);

  const connectReward = currentRetailer?.retailerConnectRewardAmount ?? 0;

  const errorType = interactionRequirement?.type;

  const isOtpError = errorType === UserInteractionType.OTP_VERIFICATION;
  const isCredentialsError =
    errorType === UserInteractionType.RETAILER_CREDENTIALS;

  const hasError = isOtpError || isCredentialsError;

  const guestCheckoutClick = () => {
    switchToGuest();
    EditConnectPopup.close();
  };

  const openEdit = () => {
    trackAction?.(
      AnalyticsTrackingEvent.CHECKOUT_CONNECT_CREDENTIALS_POPUP_OPEN,
      {
        orderId: orderId ?? "",
      }
    );
    EditConnectPopup.show({
      guestCheckoutClick: guestCheckoutClick,
      hideLeaving: true,
      defaultStatus: PopupStatus.CONNECT,
      connectReward,
      onSaveCredentials: handleSubmitCredentials,
      currentUsername:
        orderData?.userCheckoutInfo?.retailerConnection?.username || "",
      currentPassword: "",
      retailer: currentRetailer,
      checkoutIntentUsername: "",
      customSubtitle: `You’re almost there! Let’s make sure your ${
        currentRetailer?.displayName || "Retailer"
      } information is correct.`,
      errorBorder: true,
    });
  };

  const openOTP = () => {
    setOtpOpen(true);
  };

  useEffect(() => {
    trackAction?.(AnalyticsTrackingEvent.CHECKOUT_CONNECT_SCREEN_VISIT, {
      orderId: orderId ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    if (otpOpen) {
      trackAction?.(AnalyticsTrackingEvent.CHECKOUT_CONNECT_OTP_POPUP_OPEN, {
        orderId: orderId ?? "",
        displayMessage:
          interactionRequirement?.displayMessage ||
          `Enter your ${currentRetailer?.displayName ?? "retailer"} OTP code.`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpOpen]);

  const handleOTPTimeoutRetry = () => {
    retryCheckout();
    trackAction?.(AnalyticsTrackingEvent.CHECKOUT_CONNECT_OTP_TIMEOUT_RETRY, {
      orderId: orderId ?? "",
    });
  };
  const handleSubmitCredentials = async (email: string, password: string) => {
    if (isTimeOut) {
      submitUserInteractionAndRetry({
        interactionType: UserInteractionType.RETAILER_CREDENTIALS,
        interactionData: {
          credential: {
            username: email,
            password: password,
          },
          retailerId: currentRetailer?.retailerId ?? 0,
        },
      });
      trackAction?.(
        AnalyticsTrackingEvent.CHECKOUT_CONNECT_CREDENTIALS_SUBMIT,
        {
          orderId: orderId ?? "",
          username: email,
          retailerId: currentRetailer?.retailerId ?? 0,
        }
      );
      trackAction?.(
        AnalyticsTrackingEvent.CHECKOUT_CONNECT_CREDENTIALS_TIMEOUT_RETRY,
        {
          orderId: orderId ?? "",
        }
      );
    } else {
      submitUserInteraction({
        interactionType: UserInteractionType.RETAILER_CREDENTIALS,
        interactionData: {
          credential: {
            username: email,
            password: password,
          },
          retailerId: currentRetailer?.retailerId ?? 0,
        },
      });
      trackAction?.(
        AnalyticsTrackingEvent.CHECKOUT_CONNECT_CREDENTIALS_SUBMIT,
        {
          orderId: orderId ?? "",
          username: email,
          retailerId: currentRetailer?.retailerId ?? 0,
        }
      );
    }
  };

  const handleSubmitOTP = async () => {
    submitUserInteraction({
      interactionType: UserInteractionType.OTP_VERIFICATION,
      interactionData: {
        otpCode: otp,
      },
    });
    setOtpOpen(false);
    trackAction?.(AnalyticsTrackingEvent.CHECKOUT_CONNECT_OTP_SUBMIT, {
      orderId: orderId ?? "",
      otpCode: otp,
    });
  };

  const handleResend = () => {
    setResendCoolDown(30);
    submitUserInteraction({
      interactionType: UserInteractionType.OTP_VERIFICATION,
      interactionData: {
        resend: true,
      },
    });
    trackAction?.(AnalyticsTrackingEvent.CHECKOUT_CONNECT_OTP_RESEND, {
      orderId: orderId ?? "",
    });
  };

  const handleChange = (otp: string) => {
    setOtp(otp);
  };

  useEffect(() => {
    if (isOtpError) {
      setOtpOpen(true);
    }
  }, [isOtpError]);

  const timeoutTitle = `Your connection timed out`;
  const errorTittle = "Your connection failed";

  const otpTimeoutSubtitle = `You’re almost there! Give it another go — just tap the link below and wait for your ${
    currentRetailer?.displayName || "Retailer"
  } verification code.`;
  const errorSubtitle = `We couldn’t connect to your ${
    currentRetailer?.displayName || "Retailer"
  } account — just tap the link below to update your details.`;

  const title = isTimeOut && isOtpError ? timeoutTitle : errorTittle;

  const subtitle = isTimeOut && isOtpError ? otpTimeoutSubtitle : errorSubtitle;

  const buttonCta = isTimeOut && isOtpError ? "Try again" : "Update details";

  return (
    <RetailerConnectContainer>
      <div className="icons">
        <img
          alt="bc-icon"
          src={
            "https://media.brandclub.com/brandclub/image_asset/brandclub-b-logo.webp"
          }
        />
        <div>
          <DotAnimation loading={true} error={hasError} />
        </div>
        <img alt="r-icon" src={getEntityImage(retailerId, "retailer")} />
      </div>
      {hasError ? (
        <div className="desc">
          <div className="title">{title}</div>
          <div className="desc">{subtitle}</div>
          <SolidButton
            onClick={
              isOtpError && isTimeOut
                ? handleOTPTimeoutRetry
                : isCredentialsError
                ? openEdit
                : openOTP
            }
            color="primary"
            sx={(theme) => ({
              width: "100%",
              marginTop: "12px",
              maxWidth: 380,
              fontSize: 16,
              height: 50,
              [theme.breakpoints.down("md")]: {
                width: "100%",
              },
            })}
          >
            {buttonCta}
          </SolidButton>
          {/* {enabledGuestCheckout ? (
            <div
              className="switch-to-guest"
              role="button"
              onClick={guestCheckoutClick}
            >
              Switch to guest checkout
            </div>
          ) : null} */}
        </div>
      ) : (
        <div className="desc">
          <div className="title">Confirming connection</div>
          <div className="desc">
            We’re sending your order to {getRetailerFromId(+retailerId)} — hang
            tight for 10 seconds while we verify your connection.
          </div>
        </div>
      )}

      <OTPPopup
        userInteractionRequirement={interactionRequirement}
        open={otpOpen}
        onClose={() => {
          setOtpOpen(false);
        }}
        value={otp}
        onChange={handleChange}
        onSave={handleSubmitOTP}
        retailer={currentRetailer}
        onResend={handleResend}
        resendCoolDown={resendCoolDown}
      />
    </RetailerConnectContainer>
  );
};

export const OrderConfirmation: React.FC<OrderConfirmationProps> = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");
  const [connecting, setConnecting] = useState(true);
  const navigate = useNavigate();
  const [minimumTimeElapsed, setMinimumTimeElapsed] = useState(false);

  const { routePrefix } = useSiteContext();
  const appConfig = useAppSelector((state) => state.appConfig);
  const onDTC = appConfig?.domainConfig.storeBrandingType === "CustomDTCStore";

  const purchaseDetailUrlPrefix = onDTC
    ? `/purchase/`
    : routePrefix
    ? `${routePrefix}/branddashboard/purchase/`
    : `/dashboard/purchase/`;

  // Use the order tracking hook
  const {
    orderData,
    isLoading,
    isWaiting,
    noConnection,
    startTracking,
    stopTracking,
    retryCheckout,
    switchToGuest,
    submitUserInteraction,
    submitUserInteractionAndRetry,
  } = useOrderTracking(orderId || "");

  const showConnectPage = orderData?.showConnectPage;

  // Navigation function to go to checkout summary
  const handleTrackOrderClick = () => {
    navigate(`/checkout/connectSuccess?orderId=${orderId}`);
  };

  const isTimeOut =
    orderData?.userRetailerCheckoutOrder?.status ===
    UserRetailerCheckoutOrderStatusV2.user_interaction_timeout;

  useEffect(() => {
    const timer = setTimeout(() => {
      setMinimumTimeElapsed(true);
    }, 4000);

    return () => clearTimeout(timer);
  }, []);

  const orderState = orderData?.userRetailerCheckoutOrder?.status;

  useEffect(() => {
    if (orderState === UserRetailerCheckoutOrderStatusV2.checkout_error) {
      navigate(purchaseDetailUrlPrefix + orderId);
    } else if (
      orderState === UserRetailerCheckoutOrderStatusV2.user_interaction_timeout
    ) {
      navigate(`/checkoutSummary?orderId=${orderId}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderState]);

  useEffect(() => {
    if (minimumTimeElapsed && showConnectPage && isLoading && isWaiting) {
      setConnecting(false);
    }
  }, [minimumTimeElapsed, showConnectPage, isLoading, isWaiting]);

  useEffect(() => {
    if (orderId === null) {
      console.warn("no order id found in the URL; navigating to purchase");
      navigate("/dashboard/purchase");
      return;
    }

    startTracking();

    // Cleanup on unmount
    return () => {
      stopTracking();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    if (!connecting && noConnection) {
      navigate("/dashboard/purchase");
    } else if (orderData && !noConnection && !showConnectPage) {
      handleTrackOrderClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData, noConnection, showConnectPage]);

  const userInteractionRequirement =
    orderData?.userRetailerCheckoutOrder?.userInteractionRequirement;

  if (
    !orderData?.userRetailerCheckoutOrder?.orderId ||
    showConnectPage === false
  ) {
    return null;
  }

  return (
    <FlowContainer
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        padding: 3,
        [`@media (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
          justifyContent: "center",
        },
        [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
          justifyContent: "center",
          minHeight: `calc(100dvh - ${mobileHeaderHeight}px)`,
        },
      }}
    >
      <RetailerConnection
        isTimeOut={isTimeOut}
        switchToGuest={switchToGuest}
        retryCheckout={retryCheckout}
        orderData={orderData?.userRetailerCheckoutOrder}
        interactionRequirement={userInteractionRequirement}
        submitUserInteraction={submitUserInteraction}
        submitUserInteractionAndRetry={submitUserInteractionAndRetry}
      />
    </FlowContainer>
  );
};

export default OrderConfirmation;
